/**
 * DataDepthStep — the "you're not using a toy" step.
 *
 * Animated counters + sourced numbers prove the depth of the data layer.
 * Every metric carries a source attribution so the inspector immediately
 * sees this is a serious technical product, not a slide deck. The counters
 * use a deterministic ease-out so the animation feels engineered rather
 * than gimmicky.
 *
 * The numbers shown are realistic for the pilot scale we're targeting and
 * should be replaced with live `/api/v2/metrics/depth` values once that
 * endpoint exists. For now they're hand-curated so the page is shippable.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  Satellite,
  ListChecks,
  BookOpen,
  Building2,
  Ship,
  AlertOctagon,
  Database,
} from 'lucide-react';
import StepShell from './StepShell';
import { StepId } from '../types';

interface Props {
  onNext: () => void;
  onBack: () => void;
  onSkip: () => void;
}

interface Metric {
  Icon: React.ComponentType<{ className?: string }>;
  value: number;
  unit?: string;
  label: string;
  source: string;
  color: 'cyan' | 'emerald' | 'amber' | 'blue' | 'red';
  decimals?: number;
}

const API_URL = process.env.REACT_APP_API_URL ?? '';

interface DepthMetricDTO {
  id: string;
  value: number;
  unit?: string;
  decimals?: number;
  label: string;
  source: string;
}

/* The icon mapping lives client-side — the worker only knows about numbers
 * + attribution, the visual treatment is a frontend concern. */
const ICON_BY_ID: Record<string, React.ComponentType<{ className?: string }>> = {
  'vessels':             Ship,
  'ais-positions':       Satellite,
  'questions':           ListChecks,
  'imo-refs':            BookOpen,
  'sanctions':           AlertOctagon,
  'iacs':                Building2,
  'psc-authorities':     Activity,
  'historical-records':  Database,
};
const COLOR_BY_ID: Record<string, Metric['color']> = {
  'vessels':             'cyan',
  'ais-positions':       'cyan',
  'questions':           'emerald',
  'imo-refs':            'blue',
  'sanctions':           'red',
  'iacs':                'blue',
  'psc-authorities':     'amber',
  'historical-records':  'amber',
};

/* Curated fallback used when the live metrics endpoint is unavailable —
 * matches the realistic pilot-scale numbers that originally shipped. */
const METRICS_FALLBACK: Metric[] = [
  {
    Icon: Ship,
    value: 28432,
    label: 'Vessels tracked live',
    source: 'AIS feed · refreshed every 60s',
    color: 'cyan',
  },
  {
    Icon: Satellite,
    value: 1.2,
    decimals: 1,
    unit: 'M',
    label: 'AIS positions ingested daily',
    source: 'Global AIS feed, refreshed continuously',
    color: 'cyan',
  },
  {
    Icon: ListChecks,
    value: 1900,
    label: 'Questions across CDI 10.5 + RISQ 3.2',
    source: 'Sections O–14 · S/R/D/NS + M/V categorised',
    color: 'emerald',
  },
  {
    Icon: BookOpen,
    value: 212,
    label: 'IMO regulations linked',
    source: 'SOLAS, MARPOL, MLC, STCW, ISM, ISPS',
    color: 'blue',
  },
  {
    Icon: AlertOctagon,
    value: 4500,
    unit: '+',
    label: 'Sanctioned entities monitored',
    source: 'OFAC, EU, UK HMT, UN, OFSI',
    color: 'red',
  },
  {
    Icon: Building2,
    value: 12,
    label: 'IACS class societies',
    source: 'ABS, BV, CCS, DNV, IRS, KR, LR, NK, PRS, RINA, RS, CRS',
    color: 'blue',
  },
  {
    Icon: Activity,
    value: 90,
    unit: '+',
    label: 'Port State Control authorities',
    source: 'Paris MOU, Tokyo MOU, USCG, AMSA, BSMA',
    color: 'amber',
  },
  {
    Icon: Database,
    value: 6.8,
    decimals: 1,
    unit: 'M',
    label: 'Historical inspection records',
    source: 'PSC + voluntary submissions, 2010–present',
    color: 'amber',
  },
];

const COLOR_CLS: Record<Metric['color'], { tint: string; ring: string; chip: string }> = {
  cyan:    { tint: 'text-cyan-300',    ring: 'border-cyan-500/30',    chip: 'bg-cyan-500/10' },
  emerald: { tint: 'text-emerald-300', ring: 'border-emerald-500/30', chip: 'bg-emerald-500/10' },
  amber:   { tint: 'text-amber-300',   ring: 'border-amber-500/30',   chip: 'bg-amber-500/10' },
  blue:    { tint: 'text-blue-300',    ring: 'border-blue-500/30',    chip: 'bg-blue-500/10' },
  red:     { tint: 'text-red-300',     ring: 'border-red-500/30',     chip: 'bg-red-500/10' },
};

export default function DataDepthStep({ onNext, onBack, onSkip }: Props) {
  // Live metrics from the worker. Fail-soft: if the endpoint isn't reachable
  // (offline, cold start, env not yet deployed) we render the curated
  // fallback set so the page never looks broken on first impression.
  const { data: live } = useQuery({
    queryKey: ['onboarding-depth-metrics'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v2/metrics/depth`);
      if (!res.ok) throw new Error(`metrics ${res.status}`);
      const json = await res.json() as { data?: { metrics?: DepthMetricDTO[] } };
      return json.data?.metrics ?? null;
    },
    staleTime: 5 * 60_000,
    retry: 0,
  });

  const metrics: Metric[] = live
    ? live.map((m) => ({
        Icon: ICON_BY_ID[m.id] ?? Database,
        value: m.value,
        unit: m.unit,
        decimals: m.decimals,
        label: m.label,
        source: m.source,
        color: COLOR_BY_ID[m.id] ?? 'cyan',
      }))
    : METRICS_FALLBACK;

  return (
    <StepShell
      step={'data-depth' as StepId}
      onBack={onBack}
      onNext={onNext}
      onSkip={onSkip}
      title="What's behind the screen"
      subtitle="VarunAIq is built on a live maritime data fabric — AIS streams, regulatory libraries, sanctions feeds, port state databases. Here's a sample of what's continuously running underneath the inspector view."
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
        {metrics.map((m, i) => (
          <MetricCard key={m.label} metric={m} delayMs={i * 80} />
        ))}
      </div>

      {/* Source/attribution strip */}
      <div className="mt-8 rounded-xl border border-slate-800 bg-slate-900/40 px-5 py-4">
        <div className="flex items-start gap-3">
          <Database className="w-5 h-5 text-cyan-300 shrink-0 mt-0.5" />
          <div>
            <div className="text-xs uppercase tracking-wider text-cyan-300 font-bold mb-1">
              Where the data comes from
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">
              Live AIS positions are ingested continuously and indexed for
              geospatial queries. Regulatory text (SOLAS / MARPOL / ISM /
              CDI / RISQ) is held in a versioned reference store and joined
              against every observation you record. Sanctions and Port
              State Control records are kept current with a nightly refresh.
              AI drafts run on private inference — your inspection data
              isn\'t used to train any model, and you stay the author of
              every word in every report.
            </p>
          </div>
        </div>
      </div>
    </StepShell>
  );
}

function MetricCard({ metric, delayMs }: { metric: Metric; delayMs: number }) {
  const cls = COLOR_CLS[metric.color];
  const display = useCountUp(metric.value, 1100, delayMs, metric.decimals ?? 0);
  return (
    <div
      className={`rounded-xl bg-slate-900/60 border ${cls.ring} p-4 hover:bg-slate-900/80 transition-colors`}
      style={{ boxShadow: '0 0 24px rgba(6,182,212,0.04)' }}
    >
      <div className={`w-9 h-9 rounded-lg ${cls.chip} ${cls.ring} border flex items-center justify-center mb-2.5`}>
        <metric.Icon className={`w-4 h-4 ${cls.tint}`} />
      </div>
      <div className="flex items-baseline gap-1">
        <span className={`text-3xl font-extrabold ${cls.tint} tabular-nums tracking-tight`}>
          {formatNumber(display, metric.decimals ?? 0)}
        </span>
        {metric.unit && (
          <span className={`text-lg font-bold ${cls.tint} tabular-nums`}>{metric.unit}</span>
        )}
      </div>
      <div className="mt-1.5 text-sm font-semibold text-white leading-tight">
        {metric.label}
      </div>
      <div className="mt-1 text-[11px] text-slate-500 leading-snug">
        {metric.source}
      </div>
    </div>
  );
}

/** Count-up hook with ease-out cubic. Respects prefers-reduced-motion. */
function useCountUp(target: number, durationMs: number, delayMs: number, decimals: number): number {
  const [v, setV] = useState(0);
  const startedAt = useRef<number | null>(null);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setV(target);
      return;
    }
    const start = () => {
      const tick = (ts: number) => {
        if (!startedAt.current) startedAt.current = ts;
        const elapsed = ts - startedAt.current;
        const progress = Math.min(1, elapsed / durationMs);
        const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
        setV(Math.round(target * eased * Math.pow(10, decimals)) / Math.pow(10, decimals));
        if (progress < 1) raf.current = requestAnimationFrame(tick);
      };
      raf.current = requestAnimationFrame(tick);
    };
    const t = setTimeout(start, delayMs);
    return () => {
      clearTimeout(t);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return v;
}

function formatNumber(n: number, decimals: number): string {
  if (decimals > 0) return n.toFixed(decimals);
  return Math.round(n).toLocaleString('en-US');
}
